import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  MapPin,
  Calendar as CalendarIcon,
  UserCircle,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  X,
  MessageSquare,
  Send,
  Trash2,
} from 'lucide-react';
import { commentsAPI, uploadsAPI } from '../../api/client';
import useAuthStore from '../../store/useAuthStore';
import { useToast } from '../ToastProvider';
import noImage from '../../../assets/no-image.webp';

/* ─────────────────────────────────────────────────────────────
   Вспомогательные функции
   ───────────────────────────────────────────────────────────── */
function getPhotoSrc(photo) {
  if (!photo) return null;
  const filename =
    typeof photo === 'string'
      ? photo
      : photo.file_id ||
        photo.filename ||
        (photo.local_path ? photo.local_path.replace('/app/photos/', '') : null);
  return filename ? uploadsAPI.getPhoto(filename) : null;
}

function handleImgError(e) {
  e.currentTarget.onerror = null;
  e.currentTarget.src = noImage;
}

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseEntityInfo(cardData) {
  if (!cardData) return null;
  if (cardData.entityType) {
    const fromNumber = parseInt(cardData.number, 10);
    if (Number.isFinite(fromNumber)) {
      return { entityType: cardData.entityType, entityId: fromNumber };
    }
  }
  const id = cardData.id;
  if (!id) return null;
  const str = String(id);
  const lastDash = str.lastIndexOf('-');
  if (lastDash === -1) return null;
  const entityType = str.substring(0, lastDash);
  const entityId = parseInt(str.substring(lastDash + 1), 10);
  return Number.isFinite(entityId) && entityType ? { entityType, entityId } : null;
}

/* ─────────────────────────────────────────────────────────────
   Lightbox для просмотра фото внутри оверлея
   ───────────────────────────────────────────────────────────── */
function PhotoLightbox({ photos, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const src = getPhotoSrc(photos[index]) || noImage;
  const canPrev = index > 0;
  const canNext = index < photos.length - 1;

  const overlay = (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center w-screen min-h-[100dvh]"
      style={{ background: 'rgba(0,0,0,0.96)' }}
    >
      <button
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white"
        onClick={onClose}
      >
        <X className="w-6 h-6" />
      </button>
      {photos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/40 text-white text-[13px] px-3 py-1 rounded-full">
          {index + 1} / {photos.length}
        </div>
      )}
      {canPrev && (
        <button
          className="absolute left-2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white"
          onClick={() => setIndex((i) => i - 1)}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {canNext && (
        <button
          className="absolute right-2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white"
          onClick={() => setIndex((i) => i + 1)}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}
      <img
        src={src}
        onError={handleImgError}
        alt=""
        className="max-w-full object-contain max-h-[var(--app-visual-height,100dvh)]"
      />
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  return createPortal(overlay, document.body);
}

/* ─────────────────────────────────────────────────────────────
   Полноэкранный оверлей с деталями карточки
   ───────────────────────────────────────────────────────────── */
function CardDetailOverlay({ cardData, onClose }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSending, setCommentSending] = useState(false);

  const { user: currentUser } = useAuthStore();
  const { confirm, showToast } = useToast();
  const currentUserId = currentUser?.userId ?? currentUser?.id;
  const isAdmin = currentUser?.role === 'admin';
  const entityInfo = parseEntityInfo(cardData);

  const {
    title,
    status,
    address,
    creatorName,
    createdAt,
    text,
    meta,
    extraFields,
    photos: rawPhotos,
    pageTitle,
    number,
    id,
  } = cardData;

  const photos = Array.isArray(rawPhotos) ? rawPhotos : [];
  const cardNumber = number ?? (String(id || '').split('-').pop() || '???');

  useEffect(() => {
    if (!entityInfo) {
      setComments([]);
      return undefined;
    }
    let cancelled = false;
    setCommentsLoading(true);
    commentsAPI.getComments(entityInfo.entityType, entityInfo.entityId)
      .then((res) => {
        if (!cancelled) setComments(res.data.comments || []);
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityInfo?.entityType, entityInfo?.entityId]);

  const handleSendComment = async () => {
    if (!commentText.trim() || !entityInfo || commentSending) return;
    setCommentSending(true);
    try {
      const res = await commentsAPI.postComment(
        entityInfo.entityType,
        entityInfo.entityId,
        commentText.trim()
      );
      setComments((prev) => [...prev, res.data.comment]);
      setCommentText('');
    } catch {
      showToast?.('Не удалось отправить комментарий', { type: 'error' });
    } finally {
      setCommentSending(false);
    }
  };

  const handleDeleteComment = async (comment) => {
    if (!comment?.id) return;
    const ok = await confirm?.({
      title: 'Удалить комментарий',
      message: 'Действие нельзя отменить.',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    try {
      await commentsAPI.deleteComment(comment.id);
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
    } catch {
      showToast?.('Не удалось удалить комментарий', { type: 'error' });
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col bg-[#f8f9fc]">
      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-100 flex items-center gap-3 px-3 py-2.5 shrink-0 shadow-sm">
        <button
          type="button"
          className="w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 active:scale-95 transition-all"
          onClick={onClose}
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-bold text-slate-900 leading-none">
            Карточка №{cardNumber}
          </div>
          {pageTitle && (
            <div className="text-[12px] text-slate-400 mt-0.5 truncate">{pageTitle}</div>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* Main block */}
        <div className="bg-white p-4 mb-2 shadow-sm border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            {status && (
              <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                {status}
              </span>
            )}
          </div>

          <h2 className="text-[20px] font-bold text-slate-900 leading-snug mb-4">{title}</h2>

          <div className="space-y-2.5 mb-5">
            {creatorName && (
              <div className="flex items-start gap-2.5 text-[13px] text-slate-600">
                <UserCircle className="w-[18px] h-[18px] text-slate-400 shrink-0" />
                <div>
                  <span className="text-slate-400">Создатель:</span>{' '}
                  <span className="font-medium text-slate-800">{creatorName}</span>
                </div>
              </div>
            )}
            {createdAt && (
              <div className="flex items-start gap-2.5 text-[13px] text-slate-600">
                <CalendarIcon className="w-[18px] h-[18px] text-slate-400 shrink-0" />
                <div>
                  <span className="text-slate-400">Создано:</span>{' '}
                  <span className="font-medium text-slate-800">{formatDate(createdAt)}</span>
                </div>
              </div>
            )}
            {address && (
              <div className="flex items-start gap-2.5 text-[13px] text-slate-600">
                <MapPin className="w-[18px] h-[18px] text-slate-400 shrink-0 mt-0.5" />
                <div className="leading-tight">
                  <span className="text-slate-400">Площадка:</span>{' '}
                  <span className="font-medium text-slate-800">{address}</span>
                </div>
              </div>
            )}
          </div>

          {meta && (
            <div className="bg-slate-50 rounded-xl px-3.5 py-2.5 mb-4 text-[13px] text-slate-600">
              {meta}
            </div>
          )}

          {Array.isArray(extraFields) && extraFields.length > 0 && (
            <div className="space-y-1 mb-4">
              {extraFields.map((f, i) => (
                <div key={i} className="text-[13px] text-slate-600">
                  <span className="text-slate-400">{f.label}:</span>{' '}
                  <span className="font-medium text-slate-800">{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {text && (
            <div className="text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 p-3.5 rounded-xl border border-slate-100">
              {text}
            </div>
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="bg-white p-4 mb-2 shadow-sm border-y border-slate-100">
            <h3 className="text-[15px] font-bold text-slate-900 mb-3 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-slate-400" />
              Фотографии ({photos.length})
            </h3>
            <div
              className="flex gap-2.5 overflow-x-auto pb-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {photos.map((photo, idx) => {
                const src = getPhotoSrc(photo);
                return (
                  <button
                    key={idx}
                    type="button"
                    className="w-[120px] h-[120px] shrink-0 rounded-[12px] bg-slate-100 border border-slate-200 overflow-hidden active:scale-95 transition-transform"
                    onClick={() => setLightboxIndex(idx)}
                  >
                    <img
                      src={src || noImage}
                      onError={handleImgError}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="bg-white p-4 mb-2 shadow-sm border-y border-slate-100 min-h-[150px]">
          <h3 className="text-[15px] font-bold text-slate-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-400" />
            Комментарии{comments.length > 0 ? ` · ${comments.length}` : ''}
          </h3>
          {!entityInfo ? (
            <p className="text-center text-slate-400 text-[13px] py-4">
              Для этой карточки комментарии недоступны
            </p>
          ) : commentsLoading ? (
            <div className="flex justify-center py-6">
              <div className="w-5 h-5 border-2 border-[#1890ff] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-center text-slate-400 text-[13px] py-4">
              Комментариев пока нет. Напишите первый!
            </p>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => {
                const canDelete = isAdmin || (currentUserId != null && comment.user_id === currentUserId);
                return (
                  <div key={comment.id} className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">
                      {comment.user_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between mb-0.5 gap-2">
                        <span className="font-semibold text-[13px] text-slate-900 truncate">
                          {comment.user_name || 'Пользователь'}
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[10px] text-slate-400">{formatDate(comment.created_at)}</span>
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => handleDeleteComment(comment)}
                              className="text-slate-400 hover:text-rose-500 active:scale-90 transition-colors"
                              aria-label="Удалить комментарий"
                              title="Удалить"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="bg-slate-50 text-[13px] text-slate-700 px-3 py-2 rounded-2xl rounded-tl-sm border border-slate-100 leading-snug whitespace-pre-wrap">
                        {comment.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {entityInfo && (
        <div
          className="shrink-0 bg-white border-t border-slate-200 px-3 py-2.5 flex items-center gap-2"
          style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }}
        >
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendComment();
              }
            }}
            className="flex-1 min-w-0 border border-gray-300 rounded-full px-4 py-2.5 text-[15px] focus:outline-none focus:border-[#1890ff] transition-colors bg-white"
            placeholder="Оставить комментарий..."
            disabled={commentSending}
          />
          <button
            type="button"
            onClick={handleSendComment}
            disabled={!commentText.trim() || commentSending}
            className="bg-[#1890ff] flex items-center justify-center text-white w-10 h-10 rounded-full active:scale-95 transition-transform hover:bg-blue-600 shadow-sm shrink-0 disabled:opacity-50 disabled:pointer-events-none"
            aria-label="Отправить комментарий"
          >
            <Send className="w-5 h-5 ml-0.5" strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Миниатюра карточки в чате
   ───────────────────────────────────────────────────────────── */
export default function SharedCardBubble({ messageText }) {
  const [showDetail, setShowDetail] = useState(false);

  // Parse JSON card data — устойчиво к: объекту, двойному stringify, null/undefined
  let cardData = null;
  try {
    if (messageText && typeof messageText === 'object') {
      // Уже объект (не строка)
      cardData = messageText;
    } else if (typeof messageText === 'string') {
      const parsed = JSON.parse(messageText);
      // Двойной stringify: JSON.parse вернул строку — парсим ещё раз
      cardData = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
    }
  } catch {
    return <div className="message-text">{typeof messageText === 'string' ? messageText : ''}</div>;
  }

  if (!cardData || typeof cardData !== 'object') {
    return <div className="message-text">{typeof messageText === 'string' ? messageText : ''}</div>;
  }

  const { title, status, photos: rawPhotos, pageTitle, number, id, address } = cardData;
  const photos = Array.isArray(rawPhotos) ? rawPhotos : [];
  const cardNumber = number ?? (String(id || '').split('-').pop() || '???');
  const firstPhoto = photos[0];
  const firstPhotoSrc = firstPhoto ? getPhotoSrc(firstPhoto) : null;

  return (
    <>
      {showDetail &&
        createPortal(
          <CardDetailOverlay cardData={cardData} onClose={() => setShowDetail(false)} />,
          document.body
        )}

      {/* Thumbnail button */}
      <button
        type="button"
        className="block w-full text-left rounded-[14px] bg-white border border-slate-200 shadow-sm active:scale-[0.98] transition-transform"
        style={{ maxWidth: 220, minWidth: 180 }}
        onClick={() => setShowDetail(true)}
      >
        {/* Photo */}
        <div className="w-full h-[110px] bg-slate-100 overflow-hidden">
          <img
            src={firstPhotoSrc || noImage}
            onError={handleImgError}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info */}
        <div className="px-2.5 pt-2 pb-2.5">
          {/* Section label */}
          <div className="text-[10px] font-bold text-[#1890ff] uppercase tracking-wider mb-1 truncate">
            📌 {pageTitle || 'Карточка'}
          </div>

          {/* Status + number */}
          <div className="flex items-center gap-1.5 mb-1">
            {status && (
              <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0">
                {status}
              </span>
            )}
            <span className="text-[11px] text-slate-400 shrink-0">№{cardNumber}</span>
          </div>

          {/* Title */}
          <div className="text-[13px] font-semibold text-slate-900 leading-tight line-clamp-2">
            {title || `Карточка №${cardNumber}`}
          </div>

          {/* Address */}
          {address && (
            <div className="flex items-center gap-1 mt-1.5">
              <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
              <span className="text-[11px] text-slate-500 truncate">{address}</span>
            </div>
          )}
        </div>
      </button>
    </>
  );
}
